'use client';

import { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Dialog, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { fileSize } from '@/lib/inbox/display';
import { blob } from '@/lib/fetch';
import { shareFile, withExtension } from '@/lib/app/shareFile';
import PdfPages from '@/components/PdfPages';
import type { InboxAttachment, InboxMessage } from '@/lib/inbox/types';
import { INK, MUTED, RULE, SURFACE, WASH } from './ConversationList';
import { useSheetDrag } from './SheetDrag';
import { enter, fadeIn, press } from './motion';

const IMAGE = /^image\/(png|jpe?g|gif|webp|avif|bmp|heic|heif)$/i;

type Linked = InboxAttachment & { url: string };

export type ViewerFile = { url: string; name: string; mime?: string };

export function fileKind(file: { name: string }) {
  const extension = file.name.split('.').pop();
  return extension && extension !== file.name ? extension.toUpperCase() : 'File';
}

export function isImageFile(file: { mime?: string }) {
  return Boolean(file.mime && IMAGE.test(file.mime));
}

export function isPdfFile(file: { name?: string; mime?: string }) {
  return file.mime === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}

export async function downloadInPlace(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name || 'download';
  link.rel = 'noopener';
  link.target = '_blank';
  try {
    // A link to another origin, like S3, ignores `download` and opens the file, so save the bytes instead.
    const data = await blob(new URL(url, window.location.href).href);
    if (data) {
      link.href = URL.createObjectURL(data);
      link.download = withExtension(link.download, data.type);
    }
  } catch {
    // A bucket without CORS cannot be read here. The plain link still opens the file.
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (link.href.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function useVisibleFrame(open: boolean) {
  const [frame, setFrame] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    if (!open) {
      setFrame(null);
      return;
    }
    const focused = document.activeElement;
    if (focused instanceof HTMLElement) focused.blur();
    const visual = window.visualViewport;
    if (!visual) return;
    const update = () => setFrame({ top: visual.offsetTop, height: visual.height });
    update();
    visual.addEventListener('resize', update);
    visual.addEventListener('scroll', update);
    return () => {
      visual.removeEventListener('resize', update);
      visual.removeEventListener('scroll', update);
    };
  }, [open]);
  return frame;
}

export function MediaViewer({ files, index, onClose }: { files: ViewerFile[]; index: number | null; onClose: () => void }) {
  const strip = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const drag = useSheetDrag({ onDismiss: onClose });
  const frame = useVisibleFrame(index !== null);

  useEffect(() => {
    if (index === null) return;
    setCurrent(index);
    requestAnimationFrame(() => strip.current?.scrollTo({ left: index * strip.current.clientWidth }));
  }, [index]);

  const file = files[current];
  const sharable = typeof navigator !== 'undefined' && 'share' in navigator;
  const share = async () => {
    if (!file) return;
    await shareFile(file.url, file.name).catch(() => undefined);
  };

  return (
    <Dialog
      fullScreen
      disableAutoFocus
      disableScrollLock
      open={index !== null}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: '#000',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          ...(frame ? { position: 'fixed', top: frame.top, left: 0, right: 0, height: frame.height, maxHeight: frame.height } : {}),
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: '#000' } } }}
    >
      <Box {...drag} sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          component="header"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pt: 'calc(env(safe-area-inset-top) + 6px)',
            pb: 1,
            px: 1,
            background: 'linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0))',
          }}
        >
          <ButtonBase aria-label="Close" onClick={onClose} sx={{ ...press, width: 44, height: 44, borderRadius: '50%', color: '#fff' }}>
            <CloseRoundedIcon />
          </ButtonBase>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
              {file?.name}
            </Typography>
            {files.length > 1 && <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}>{`${current + 1} of ${files.length}`}</Typography>}
          </Box>
          <ButtonBase aria-label="Download" onClick={() => file && downloadInPlace(file.url, file.name)} sx={{ ...press, width: 44, height: 44, borderRadius: '50%', color: '#fff' }}>
            <DownloadRoundedIcon />
          </ButtonBase>
          {sharable && (
            <ButtonBase aria-label="Share" onClick={share} sx={{ ...press, width: 44, height: 44, borderRadius: '50%', color: '#fff' }}>
              <IosShareRoundedIcon />
            </ButtonBase>
          )}
        </Box>
        <Box
          ref={strip}
          onScroll={(event) => {
            const width = event.currentTarget.clientWidth;
            if (width) setCurrent(Math.round(event.currentTarget.scrollLeft / width));
          }}
          sx={{
            flex: 1,
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            touchAction: 'pan-x',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {files.map((item) => {
            const picture = isImageFile(item);
            const pdf = isPdfFile(item);
            return (
              <Box
                key={item.url}
                sx={{
                  flex: '0 0 100%',
                  height: '100%',
                  scrollSnapAlign: 'center',
                  display: 'flex',
                  alignItems: picture ? 'center' : 'stretch',
                  justifyContent: 'center',
                  px: 0,
                  pt: 'calc(env(safe-area-inset-top) + 56px)',
                  pb: picture ? 'calc(env(safe-area-inset-top) + 56px)' : 0,
                }}
              >
                {picture ? (
                  <Box component="img" src={item.url} alt={item.name} draggable={false} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none', ...enter(fadeIn, 200) }} />
                ) : pdf ? (
                  <PdfPages url={item.url} title={item.name} />
                ) : (
                  <Box sx={{ m: 'auto', px: 3, textAlign: 'center', ...enter(fadeIn, 200) }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{item.name}</Typography>
                    <ButtonBase
                      onClick={() => downloadInPlace(item.url, item.name)}
                      sx={{ ...press, mt: 2, px: 2, py: 1, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}
                    >
                      Download
                    </ButtonBase>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
}

function FileRow({ file }: { file: InboxAttachment }) {
  const [open, setOpen] = useState(false);
  const pdf = isPdfFile(file);
  const Icon = pdf ? PictureAsPdfOutlinedIcon : InsertDriveFileOutlinedIcon;
  const details = [fileKind(file), fileSize(file.size)].filter(Boolean).join(' · ');
  const linked = Boolean(file.url);
  return (
    <>
    <ButtonBase
      onClick={linked ? () => setOpen(true) : undefined}
      disabled={!linked}
      sx={{
        ...press,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        maxWidth: 360,
        p: 1,
        pr: 1.5,
        border: `1px solid ${RULE}`,
        borderRadius: '12px',
        bgcolor: SURFACE,
        textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        '&:active': { bgcolor: WASH, transform: 'scale(0.98)' },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: '9px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: pdf ? 'color-mix(in srgb, var(--danger) 14%, transparent)' : WASH,
          color: pdf ? 'var(--danger)' : MUTED,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: INK }}>
          {file.name}
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.75rem', color: MUTED }}>
          {details}
        </Typography>
      </Box>
    </ButtonBase>
    {linked && file.url && <MediaViewer files={[{ url: file.url, name: file.name, mime: file.mime }]} index={open ? 0 : null} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function Attachments({ message }: { message: InboxMessage }) {
  const [viewing, setViewing] = useState<number | null>(null);
  if (!message.attachments.length) return null;
  const images = message.attachments.filter((file): file is Linked => Boolean(file.url && file.mime && IMAGE.test(file.mime)));
  const files = message.attachments.filter((file) => !images.includes(file as Linked));
  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {images.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: images.length === 1 ? '1fr' : 'repeat(2, 1fr)', gap: 0.75, maxWidth: 420 }}>
          {images.map((file, index) => (
            <ButtonBase
              key={file.url}
              aria-label={`View ${file.name}`}
              onClick={() => setViewing(index)}
              sx={{ ...press, position: 'relative', aspectRatio: images.length === 1 ? '4 / 3' : '1', borderRadius: '12px', overflow: 'hidden', bgcolor: WASH, WebkitTapHighlightColor: 'transparent' }}
            >
              <Box component="img" src={file.url} alt="" loading="lazy" draggable={false} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            </ButtonBase>
          ))}
        </Box>
      )}
      {files.map((file, index) => (
        <FileRow key={file.url ?? `${file.name}-${index}`} file={file} />
      ))}
      {images.length > 0 && <MediaViewer files={images} index={viewing} onClose={() => setViewing(null)} />}
    </Box>
  );
}
